/**
 * Pure section link scanner shared by the Link Manager and pre-publish audit.
 */

import { iterateAllSections } from './iterate-sections.js';

function defaultParseHTML(html) {
  if (typeof DOMParser === 'undefined') {
    throw new Error('DOMParser is not available');
  }
  return new DOMParser().parseFromString(html, 'text/html');
}

export function getDomPath(el, root) {
  const parts = [];
  let current = el;
  while (current && current !== root) {
    const parent = current.parentElement;
    if (!parent) break;
    const siblings = Array.from(parent.children).filter((c) => c.tagName === current.tagName);
    const idx = siblings.indexOf(current);
    const tag = current.tagName.toLowerCase();
    parts.unshift(siblings.length > 1 ? `${tag}:nth-of-type(${idx + 1})` : tag);
    current = parent;
  }
  return parts.join(' > ');
}

function getNearestEid(el) {
  const target = el?.closest?.('[data-pb-eid]');
  return target?.getAttribute?.('data-pb-eid') || '';
}

export function scanSectionLinks(
  html,
  pageName,
  pageId,
  sectionInstanceId,
  statePath,
  options = {}
) {
  if (!html) return [];
  const parseHTML = options.parseHTML || defaultParseHTML;
  const doc = parseHTML(`<div>${html}</div>`);
  const root = doc.body?.firstElementChild || doc.querySelector?.('div') || null;
  if (!root) return [];
  const anchors = root.querySelectorAll('a');
  const entries = [];

  anchors.forEach((a, idx) => {
    entries.push({
      pageName,
      pageId,
      sectionInstanceId,
      statePath,
      sharedKind: options.sharedKind || null,
      anchorSignature: {
        href: a.getAttribute('href') || '',
        textContent: (a.textContent || '').trim(),
        domPath: getDomPath(a, root),
        tagIndex: idx,
        className: a.getAttribute('class') || '',
        id: a.getAttribute('id') || '',
        eid: getNearestEid(a)
      }
    });
  });
  return entries;
}

export function scanAllLinks(state, options = {}) {
  const links = [];
  iterateAllSections(state, (ctx) => {
    const pageName = ctx.kind === 'page-section' ? ctx.pageName : 'All Pages';
    const pageId = ctx.kind === 'page-section' ? ctx.pageId : null;
    links.push(
      ...scanSectionLinks(ctx.html, pageName, pageId, ctx.instanceId, ctx.sectionPath, {
        parseHTML: options.parseHTML,
        sharedKind: ctx.sharedKind || null
      })
    );
  });
  return links;
}

export function findAnchorBySignature(root, sig) {
  const anchors = root.querySelectorAll('a');

  const current = Array.from(anchors).map((a, idx) => ({
    el: a,
    href: a.getAttribute('href') || '',
    text: (a.textContent || '').trim(),
    domPath: getDomPath(a, root),
    tagIndex: idx
  }));

  for (const c of current) {
    if (c.href === sig.href && c.text === sig.textContent && c.domPath === sig.domPath) {
      return { anchor: c.el, matchType: 'exact' };
    }
  }
  for (const c of current) {
    if (c.domPath === sig.domPath) {
      return { anchor: c.el, matchType: 'path' };
    }
  }
  for (const c of current) {
    if (c.href === sig.href && c.text === sig.textContent) {
      return { anchor: c.el, matchType: 'content' };
    }
  }
  if (sig.tagIndex < anchors.length) {
    return { anchor: anchors[sig.tagIndex], matchType: 'index' };
  }

  return null;
}
